import React, { useState, useEffect, createContext } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { addTranslations, setAppLocales } from '@fs/zion-locale'
import Root from '@fs/zion-root'
import { AppLayout, Theme, themes, AlertBanner } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { useExperiment } from '@fs/zion-experiments'
import { useTranslation } from 'react-i18next'
import Footer from './components/footer'
import App from './App'
import * as serviceWorker from './serviceWorker'
import translations from './locales'
import { RTCProvider } from './state/RTCStore'
import Feedback from './components/Feedback'

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: ['AbortError', 'SecurityError: The operation is insecure.'],
      denyUrls: [/bitmovin-player/],
      sampleRate: 0.2,
    })
  )
}

// For details about loading translations: https://www.familysearch.org/frontier/docs/develop/i18n
setAppLocales(['zh-hans'])
addTranslations(translations)

// This is where you pass data from the server to the client using the SERVER_DATA global.
// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const AlertBannerContext = createContext()

export default AlertBannerContext

const overrideTheme = {
  ...themes.nightfall,
  // Override nightfall's background colors to the ones used in this app, so we don't have unexpected clashing backgrounds
  styles: themes.nightfall.styles
    .replace('--background-primary:#0d0f10', '--background-primary:#000')
    .replace('--background-secondary:#212225', '--background-secondary:#222222')
    .replace('--button-icon-primary:#01b6d1', '--button-icon-primary:#FFFFFF')
    .replace('--feedback-danger-text:#fee8e7', '--feedback-danger-text:#e63939'),
}

const FrontierRoot = () => {
  const [alertBannerOpen, setAlertBannerOpen] = useState(false)
  const [alertBannerProps, setAlertBannerProps] = useState({
    message: '',
    dismissText: 'Dismiss',
    type: 'info',
  })
  const allAlertBannerProps = {
    ...alertBannerProps,
    onDismiss: (closeBanner) => closeBanner(),
    open: alertBannerOpen,
    setOpen: setAlertBannerOpen,
  }
  const { t } = useTranslation()
  const [showGenericDownBannerEx] = useExperiment('showGenericDownBannerEx')
  const [showMaintenanceDownBannerEx] = useExperiment('showMaintenanceDownBannerEx')

  useEffect(() => {
    if (showGenericDownBannerEx) {
      setAlertBannerProps({
        message: t(
          'generic-error-banner',
          'We are currently experiencing some technical challenges. Some features may not be available.'
        ),
        type: 'error',
        dismissText: t('common-ui:close.action', 'Close'),
      })
      setAlertBannerOpen(true)
    }

    if (showMaintenanceDownBannerEx) {
      setAlertBannerProps({
        message: t(
          'appPartialAlert',
          '"Some features on {app} may be unavailable as we make improvements to the site starting {weekday}, {month} {ordinalDayOfMonth} at {time} (UTC{utcPlusMinus}{utcOffset}) and lasting about {duration}.  We apologize for the inconvenience.',
          {
            app: 'RootsTech Connect',
            weekday: 'Monday',
            month: 'March',
            ordinalDayOfMonth: '15th',
            time: '12:00AM MST',
            utcPlusMinus: '-',
            utcOffset: '7',
            duration: '30 minutes',
          }
        ),
        type: 'maintenance',
        dismissText: t('common-ui:close.action', 'Close'),
      })
      setAlertBannerOpen(true)
    }
  }, [showGenericDownBannerEx, showMaintenanceDownBannerEx, t])

  return (
    <React.StrictMode>
      <RTCProvider>
        <Router basename={base}>
          <Root analytics header={false} footer={false} alertBannerProps={allAlertBannerProps}>
            <Theme theme={overrideTheme}>
              <AlertBanner {...allAlertBannerProps} />
              <AppLayout fullWidth backgroundColor="#000">
                <AlertBannerContext.Provider value={{ setAlertBannerProps, setAlertBannerOpen }}>
                  <App />
                </AlertBannerContext.Provider>
              </AppLayout>
              <Footer />
              <Feedback />
            </Theme>
          </Root>
        </Router>
      </RTCProvider>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
